import { UseQueryResult } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { DEBOUNCE_MS } from '@/components/features/UsersList/constants';
import { BaseMultiAutocomplete, BaseMultiAutocompleteProps } from '@/components/shared/Autocomplete/';
import { components } from '@/shared/api/openapi';

type User = components['schemas']['GetUsersAutocompleteEndpoint_GetUsersResponse'];

/* Shared common shape for membership */
export type MembershipItem = {
  userId?: string | null;
  isMember?: boolean;
  role?: string | null;
};

/* All membership items share same shape, we want reusability.
Checking shape changes will help to prevent potential bugs and errors. */
type AllocationGroupMembershipItem = components['schemas']['CheckAllocationGroupUserMembershipEndpoint_Response'];
type BudgetGroupMembershipItem = components['schemas']['CheckBudgetGroupUserMembershipEndpoint_Response'];
type DistributorGroupMembershipItem = components['schemas']['CheckDistributorGroupUserMembershipEndpoint_Response'];

type AssertCompatibleMembershipType<T extends MembershipItem> = T;

type CompatibleMembershipItem =
  | AssertCompatibleMembershipType<AllocationGroupMembershipItem>
  | AssertCompatibleMembershipType<BudgetGroupMembershipItem>
  | AssertCompatibleMembershipType<DistributorGroupMembershipItem>;

export type EnhancedUser = User & Pick<CompatibleMembershipItem, 'isMember' | 'role'>;

type EditAutocompleteData = Pick<UseQueryResult<User[]>, 'data' | 'isLoading'>;

type MembershipData = Pick<UseQueryResult<CompatibleMembershipItem[]>, 'data' | 'isLoading' | 'isRefetching'>;

/**
 * Props for the user-search autocomplete used inside EditPage.
 * Combines user search results with membership state to disable already-added users.
 */
export interface EditAutocompleteProps {
  /** User search results from the autocomplete query */
  usersData: EditAutocompleteData;
  /** Membership state — enriches options with `isMember`/`role`; options hidden while loading/refetching */
  membershipData: MembershipData;
  /** Called with selected `EnhancedUser[]` when the Add button is clicked */
  onApply?: NonNullable<BaseMultiAutocompleteProps<EnhancedUser>['actionButton']>['onApply'];
  disabled?: boolean;
  /** Current debounced search string — used by the parent to drive the users query */
  filter: string;
  /** Setter for the search string; debounce is handled internally */
  onFilteredSearch: React.Dispatch<React.SetStateAction<string>>;
}

function EditAutocomplete({ usersData, membershipData, onApply, onFilteredSearch, disabled }: EditAutocompleteProps) {
  const [selectedEmails, setSelectedEmails] = useState<EnhancedUser[]>([]);
  const [inputValue, setInputValue] = useState('');
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      onFilteredSearch(inputValue);
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [inputValue, onFilteredSearch]);

  const enhancedOptions: EnhancedUser[] = (() => {
    if (!usersData.data) return [];
    if (membershipData.isLoading || membershipData.isRefetching) return [];
    if (!membershipData.data) return usersData.data;

    const membershipMap = new Map(membershipData.data.map((m) => [m.userId, m]));

    return usersData.data.map((user) => {
      const membership = membershipMap.get(user.email ?? '');
      return {
        ...user,
        isMember: membership?.isMember,
        role: membership?.role,
      };
    });
  })();

  const handleInputChange = (_: React.SyntheticEvent, input: string) => {
    setInputValue(input);
  };

  const handleChange = (_: React.SyntheticEvent, newValue: EnhancedUser[]) => {
    setSelectedEmails(newValue);
  };

  const getOptionLabel = (option: EnhancedUser) => {
    return option.email || '';
  };

  const getOptionDisabled = (optionLabel: EnhancedUser) => {
    if (!membershipData.data || membershipData.isLoading || membershipData.isRefetching) {
      return false;
    }

    const isSelectedOptionDisabled = selectedEmails.some((val) => val.email === optionLabel.email);

    const option = enhancedOptions.find((u) => u.email === optionLabel.email);
    return option?.isMember || isSelectedOptionDisabled;
  };

  const getOptionKey = (option: EnhancedUser) => option.id ?? `key-${option.email}`;

  const handleApply = (values: EnhancedUser[]) => {
    onApply?.(values);
    setSelectedEmails([]);
  };

  return (
    <BaseMultiAutocomplete<EnhancedUser>
      autocomplete={{
        options: enhancedOptions,
        filterOptions: (x) => x,
        value: selectedEmails,
        inputValue: inputValue,
        loading: usersData.isLoading || membershipData.isLoading || membershipData.isRefetching,
        onChange: handleChange,
        onInputChange: handleInputChange,
        getOptionLabel: getOptionLabel,
        getOptionKey: getOptionKey,
        getOptionDisabled: getOptionDisabled,
        label: 'Select User',
        placeholder: 'Enter at least 3 characters to search',
        disabled: disabled ?? false,
      }}
      actionButton={{
        onApply: handleApply,
        buttonLabel: 'Add',
        buttonRef: buttonRef,
      }}
    />
  );
}

export default EditAutocomplete;
